/*
  CSV export of the lead register, built in the browser from the leads the page already holds (no new query, no
  wider payload). Pure functions plus one DOM helper. The reference build's csv.js was the guide for column choice,
  escaping and BOM.
*/

// Everything the register table shows, plus Industry and Country, which the table leaves out but the export carries.
// `labelId` is an intl id so the header row speaks the page's language.
export const EXPORT_COLUMNS = [
  { key: "date", labelId: "ff_dashboard.register.col_date", defaultLabel: "Date", value: (lead) => lead.activityDate },
  { key: "company", labelId: "ff_dashboard.register.col_company", defaultLabel: "Company", value: (lead) => lead.companyName },
  { key: "jobTitle", labelId: "ff_dashboard.register.col_job_title", defaultLabel: "Job title", value: (lead) => lead.jobTitle },
  { key: "seniority", labelId: "ff_dashboard.register.col_seniority", defaultLabel: "Seniority", value: (lead) => lead.seniority },
  { key: "jobFunction", labelId: "ff_dashboard.register.col_job_function", defaultLabel: "Job function", value: (lead) => lead.jobFunction },
  { key: "industry", labelId: "ff_dashboard.dim.industry", defaultLabel: "Industry", value: (lead) => lead.industry },
  { key: "country", labelId: "ff_dashboard.dim.country", defaultLabel: "Country", value: (lead) => lead.country },
  { key: "asset", labelId: "ff_dashboard.register.col_asset", defaultLabel: "Asset", value: (lead, lookups) => lookups.assetName[lead.assetKey] },
  { key: "lineItem", labelId: "ff_dashboard.register.col_line_item", defaultLabel: "Line item", value: (lead, lookups) => lookups.lineItemName[lead.lineItemId] }
];

/*
  One cell. Quotes, commas and line breaks are quoted/escaped. A cell that starts with = + - @ (or a tab/CR) would be
  run as a formula by Excel and Sheets, and these values are typed by lead-form visitors, so it is defused with a
  leading apostrophe (the OWASP CSV-injection guidance).
*/
export const csvCell = (value) => {
  let text = (value === null || value === undefined) ? "" : String(value);

  if (/^[=+\-@\t\r]/.test(text)) { text = `'${text}`; }
  if (/[",\r\n]/.test(text)) { text = `"${text.replace(/"/g, "\"\"")}"`; }

  return text;
};

/**
 * @param rows the leads to export, already filtered and in register order
 * @param labels `{ [column.key]: header text }`
 * @param lookups the model's name lookups (asset and line item names, never raw ids)
 */
export const buildCsv = (rows, labels, lookups) => {
  const lines = [EXPORT_COLUMNS.map((column) => csvCell(labels[column.key] || column.defaultLabel)).join(",")];

  rows.forEach((lead) => {
    lines.push(EXPORT_COLUMNS.map((column) => csvCell(column.value(lead, lookups))).join(","));
  });

  // The BOM makes Excel read the file as UTF-8 (accented names stay intact); CRLF is what spreadsheets expect.
  return `﻿${lines.join("\r\n")}\r\n`;
};

const pad = (n) => String(n).padStart(2, "0");

export const exportFilename = (campaignName, filtered, date = new Date()) => {
  const slug = String(campaignName || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "campaign";
  const stamp = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;

  return `${slug}-leads${filtered ? "-filtered" : ""}-${stamp}.csv`;
};

/*
  The active selection in the audit API's shape: `{ dimension: values[] }`, keys exactly the dashboard's dimension names.
  Line item ids go as numbers (the API's example), everything else as strings; sorted so the same selection is always the
  same payload. The "Unknown" bucket (null) is never sent because it cannot be selected on screen.
*/
export const filtersPayload = (filters) => (
  Object.keys(filters).reduce((payload, dimension) => {
    const values = [...filters[dimension]].map((value) => (dimension === "lineItemId" && /^\d+$/.test(value) ? Number(value) : value));

    payload[dimension] = values.sort((a, b) => (typeof a === "number" && typeof b === "number" ? a - b : String(a).localeCompare(String(b))));
    return payload;
  }, {})
);

// A v4 UUID for one export click. The audit API treats the same id as the same export, so a repeat of the call is safe.
export const newExportId = () => {
  const webCrypto = typeof crypto !== "undefined" ? crypto : null;
  if (webCrypto && typeof webCrypto.randomUUID === "function") { return webCrypto.randomUUID(); }

  // Older browsers and non-secure origins have no randomUUID.
  const bytes = new Uint8Array(16);
  if (webCrypto && typeof webCrypto.getRandomValues === "function") {
    webCrypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) { bytes[i] = Math.floor(Math.random() * 256); }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const downloadCsv = (filename, csv) => {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(url), 0);
};
